import type { ReactNode } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Breadcrumb, BreadcrumbItem } from "@/components/ui/breadcrumb";
import { cn } from "@/lib/utils";

const SMALL_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to",
]);

function toTitleCase(text: string): string {
  return text.replace(/\S+/g, (word, offset) => {
    if (offset > 0 && SMALL_WORDS.has(word.toLowerCase())) return word.toLowerCase();
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
}

interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumbItems?: BreadcrumbItem[];
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
  titleSize?: "sm" | "md" | "lg" | "xl";
  /** Render action buttons (e.g. "Create invoice") to the right of the title. */
  actions?: ReactNode;
  breadcrumbAlign?: "start" | "end";
  /** Keep the title exactly as given. Used for saved names such as a branch. */
  preserveCase?: boolean;
}

export function PageHeader({
  title,
  description,
  breadcrumbItems,
  searchQuery,
  onSearchChange,
  searchPlaceholder = "Search...",
  showSearch = true,
  titleSize = "lg",
  actions,
  breadcrumbAlign = "end",
  preserveCase = false,
}: PageHeaderProps) {
  const titleSizeClasses = {
    sm: "text-2xl",
    md: "text-3xl",
    lg: "text-4xl",
    xl: "text-5xl",
  };

  return (
    <div>
      {breadcrumbItems && breadcrumbItems.length > 0 && (
        <div
          className={cn(
            "mb-4 flex min-w-0",
            breadcrumbAlign === "start" ? "justify-start" : "justify-end"
          )}
        >
          <Breadcrumb
            items={breadcrumbItems}
            className={cn(
              "max-w-full",
              breadcrumbAlign === "start" ? "justify-start" : "justify-end"
            )}
          />
        </div>
      )}

      <div
        className={
          actions ? "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" : undefined
        }
      >
        <div className="min-w-0">
          <h1 className={`${titleSizeClasses[titleSize]} font-heading font-bold text-primary`}>
            {preserveCase ? title : toTitleCase(title)}
          </h1>
          {description && <p className="max-w-2xl mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>

      {showSearch && searchQuery !== undefined && onSearchChange && (
        <div className="mt-4 w-full sm:w-auto sm:min-w-[320px] sm:max-w-md">
          <div className="relative">
            <Search className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
            <Input
              type="search"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 transition-all bg-white border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
      )}
    </div>
  );
}
